import { Button, ButtonMode, Icon, ICONS, Pagination } from '@creditsights/styleguide-web';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Row,
  useReactTable,
  InitialTableState,
  Table as ReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames/bind';
import { toInteger } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, Fragment, useMemo, forwardRef, useImperativeHandle, ForwardedRef } from 'react';
import NoResults from '../NoResults';
import styles from './Table.module.scss';

const cx = classNames.bind(styles);

interface TableProps<TData> {
  data?: TData[];
  columns: ColumnDef<TData>[];
  expandable?: boolean;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getSubRows?: (originalRow: TData, index?: number) => TData[] | undefined;
  renderSubComponent?: (props: { row: Row<TData>; className?: string }) => ReactElement;
  renderLastExpandedComponent?: (props: { row?: Row<TData>; className?: string }) => ReactElement;
  paginated?: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page) => void;
  dataTestId: string;
  initialState?: InitialTableState;
}

export interface TableHandle<TData> {
  table: ReactTable<TData>;
}

const DEFAULT_REACT_TABLE_COLUMN_WIDTH = 150;

// This table should eventually replace the table in styleguide-web
const Table = <TData extends unknown>(
  {
    data = [],
    columns,
    expandable = false,
    getRowCanExpand,
    getSubRows,
    renderSubComponent,
    renderLastExpandedComponent,
    paginated = false,
    page = 1,
    totalPages = 1,
    onPageChange = () => {},
    dataTestId,
    initialState,
  }: TableProps<TData>,
  ref: ForwardedRef<TableHandle<TData>>
): ReactElement | null => {
  const renderExpandButton = (row: Row<TData>) => {
    if (!row.getCanExpand()) {
      return null;
    }
    return (
      <Button
        ariaLabel="expand group"
        className={cx('caret-button', {
          'is-expanded': row.getIsExpanded(),
        })}
        mode={ButtonMode.TERTIARY}
        onClick={() => row.toggleExpanded()}
        hasEqualSidesPadding
      >
        <Icon name={ICONS.CaretRight} />
      </Button>
    );
  };

  const isLastExpandedRow = (row: Row<TData>): boolean => {
    const { flatRows } = getRowModel();

    const currentIndex = flatRows.findIndex((flatRow) => flatRow.id === row.id);
    if (currentIndex < 0) {
      return false;
    }
    const nextRow = flatRows[currentIndex + 1];
    return nextRow && !!row.parentId && !nextRow.parentId;
  };

  const getIsSubRow = (row: Row<TData>): boolean => !!row.parentId;

  const getRowOrderClass = (row): string => {
    return toInteger(row.id) % 2 ? cx('even') : cx('odd');
  };

  const columnsDef: ColumnDef<TData>[] = useMemo(() => {
    if (expandable) {
      return [
        {
          id: 'expander',
          header: () => null,
          size: 32,
          meta: {
            cellProps: {
              cellClass: 'is-expandable',
            },
          },
          cell: ({ row }) => renderExpandButton(row),
        },
        ...columns,
      ];
    }
    return columns;
  }, [columns]);

  const table = useReactTable({
    data,
    columns: columnsDef,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
    initialState,
  });
  const { getHeaderGroups, getRowModel, setState, resetExpanded } = table;

  useImperativeHandle(
    ref,
    () => ({
      table,
    }),
    []
  );

  const getSizeStyle = (columnSize: number) => {
    let style;
    if (columnSize !== DEFAULT_REACT_TABLE_COLUMN_WIDTH) {
      style = {
        width: columnSize,
      };
    }
    return style;
  };

  const handlePageChange = (page: number) => {
    resetExpanded();
    onPageChange(page);
  };

  const renderPagination = () => {
    return (
      <div className={cx('footer')}>
        <Pagination activePage={page} totalPages={totalPages} onPageChange={handlePageChange} />
      </div>
    );
  };

  const showPagination = paginated && totalPages > 1;
  if (isEmpty(data)) {
    return <NoResults />;
  }
  return (
    <div className={cx('body')}>
      <table data-testid={dataTestId}>
        <thead>
          {getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const { alignRight = false, alignCenter = false } = header.column.columnDef.meta?.cellProps ?? {};
                return (
                  <th
                    {...{
                      key: header.id,
                      colSpan: header.colSpan,
                      style: getSizeStyle(header.getSize()),
                    }}
                    className={cx('th-cell', { 'align-right': alignRight, 'align-center': alignCenter })}
                  >
                    {header.isPlaceholder ? null : (
                      <div className={cx('heading')}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody data-testid={`${dataTestId}-tbody`}>
          {getRowModel().rows.map((row) => {
            return (
              <Fragment key={row.id}>
                <tr
                  className={cx(
                    'row',
                    { 'is-expanded': row.getIsExpanded(), 'row-expanded': getIsSubRow(row) },
                    getRowOrderClass(row)
                  )}
                  onMouseEnter={() => setState((prev) => ({ ...prev, hover: row.id }))}
                  onMouseLeave={() => setState((prev) => ({ ...prev, hover: null }))}
                >
                  {row.getVisibleCells().map((cell) => {
                    const {
                      alignRight = false,
                      alignCenter = false,
                      cellClass,
                    } = cell.column.columnDef.meta?.cellProps ?? {};
                    const columnClass = cx('td-cell', cellClass, {
                      'align-right': alignRight,
                      'align-center': alignCenter,
                    });
                    return (
                      <td key={cell.id} className={columnClass}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
                {isLastExpandedRow(row) &&
                  renderLastExpandedComponent &&
                  renderLastExpandedComponent({
                    row: row.getParentRow(),
                    className: cx('row-expanded', getRowOrderClass(row)),
                  })}
                {row.getIsExpanded() &&
                  renderSubComponent &&
                  renderSubComponent({ row, className: cx('row-expanded', getRowOrderClass(row)) })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {showPagination && renderPagination()}
    </div>
  );
};

export default forwardRef(Table) as <TData>(
  props: TableProps<TData> & { ref?: ForwardedRef<TableHandle<TData>> }
) => ReturnType<typeof Table>;
